import { useCallback, useRef } from 'react';

import { ApolloError } from 'apollo-client';
import { getOperationName } from 'apollo-link';
import { useIntl } from 'react-intl';

import { useGiftCardLoadMutation } from 'generated/graphql-gateway';
import {
  IPrepaidsReloadInput,
  PrepaidsReloadDocument,
  UserAccountsDocument as UserAccountsQuery,
  usePrepaidsReloadMutation,
} from 'generated/rbi-graphql';
import { ModalCb } from 'hooks/use-error-modal';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import {
  GraphQLErrorCodes,
  IErrorDialog,
  getAppleOrGooglePayError,
  getErrorDialogForGraphQLCode,
  parseGraphQLErrorCodes,
} from 'utils/errors';

import { CARD_RELOAD_ERROR, EXCEECEDS_BALANCE_LIMIT } from './constants';

const MutationName: string = getOperationName(PrepaidsReloadDocument)!;

export type IPrepaidsReload = IPrepaidsReloadInput & { prepaidBalance: number };

interface IUsePrepaidsReloadProps {
  openErrorDialog: ModalCb;
}

export const usePrepaidsReload = ({ openErrorDialog }: IUsePrepaidsReloadProps) => {
  const lastFdAccountId = useRef<string | null | undefined>(null);
  const lastPrepaidFdAccountId = useRef<string | null>(null);
  const lastPrepaidBalance = useRef<number | null>(null);
  const lastFundValue = useRef<number | null>(null);
  const logger = useLogger();
  const { refreshCurrentUser } = useAuthContext();
  const { feCountryCode } = useLocale();
  const { formatMessage } = useIntl();

  const useNewGiftCardLoadMutation = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD_LOAD_MUTATION);

  const onError = useCallback(
    (error: ApolloError) => {
      let dialog: IErrorDialog = {
        message: formatMessage({
          id: CARD_RELOAD_ERROR,
        }),
      };

      const serverErrorCode = error?.graphQLErrors?.[0]?.extensions?.code;
      const isOverBalance =
        serverErrorCode === 'OVER_ALLOWABLE_BALANCE' ||
        lastPrepaidBalance.current! + lastFundValue.current! > 10000;

      if (isOverBalance) {
        dialog.message = formatMessage({
          id: EXCEECEDS_BALANCE_LIMIT,
        });
      }

      const [errorCode] = parseGraphQLErrorCodes(error);
      if (errorCode) {
        const maybeMoreAuthoritativeDialog = getErrorDialogForGraphQLCode(errorCode, formatMessage);
        if (maybeMoreAuthoritativeDialog) {
          dialog = maybeMoreAuthoritativeDialog;
        }
        const applePayOrGooglePayError = getAppleOrGooglePayError(lastFdAccountId.current || '');

        const isPaymentError =
          errorCode.errorCode === GraphQLErrorCodes.PAYMENTS_DECLINED_ERROR ||
          errorCode.errorCode === GraphQLErrorCodes.PAYMENTS_ERROR;

        if (isPaymentError && applePayOrGooglePayError) {
          dialog.message = formatMessage({
            id: applePayOrGooglePayError,
          });
        }
      }

      openErrorDialog({
        ...dialog,
        modalAppearanceEventMessage: 'Error: Reload Prepaid Card Failure',
        error,
      });
      const { graphQLErrors: errors } = error;
      if (errors.length) {
        logger.error({
          fdAccountId: lastFdAccountId.current,
          prepaidFdAccountId: lastPrepaidFdAccountId.current,
          errors,
          message: 'error reloading prepaid card',
          MutationName,
        });
      }
    },
    [formatMessage, openErrorDialog, logger]
  );

  const [fireLegacyMutation, { loading: legacyLoading }] = usePrepaidsReloadMutation({
    awaitRefetchQueries: true,
    onError,
    refetchQueries: [
      {
        query: UserAccountsQuery,
        variables: {
          feCountryCode,
          sync: true,
        },
      },
    ],
  });

  const [fireGiftCardLoadMutation, { loading: giftCardLoading }] = useGiftCardLoadMutation({
    onError,
    refetchQueries: [
      {
        query: UserAccountsQuery,
        variables: {
          feCountryCode,
          sync: true,
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  const reloadPrepaidsCardMutation = useCallback(
    async ({
      countryCode,
      fdAccountId,
      fundValue,
      prepaidFdAccountId,
      applePayDetails,
      googlePayDetails,
      prepaidBalance,
      enableGiftCardLoad,
    }) => {
      lastFdAccountId.current = fdAccountId;
      lastPrepaidFdAccountId.current = prepaidFdAccountId;
      lastPrepaidBalance.current = +prepaidBalance;
      lastFundValue.current = +fundValue;

      if (useNewGiftCardLoadMutation && enableGiftCardLoad) {
        const result = await fireGiftCardLoadMutation({
          variables: {
            input: {
              giftCardLoadAmount: fundValue,
              paymentAccountId: fdAccountId,
            },
          },
        });

        const user = result.data?.giftCardLoad?.user;
        const prepaidAccount = user?.accounts?.find(account => account?.prepaid);
        const currentBalance = prepaidAccount?.prepaid?.currentBalance ?? undefined;

        refreshCurrentUser();

        return {
          data: {
            prepaidsReload: {
              currentBalance,
            },
          },
        };
      }

      const result = await fireLegacyMutation({
        variables: {
          input: {
            countryCode,
            fdAccountId,
            fundValue,
            prepaidFdAccountId,
            applePayDetails,
            googlePayDetails,
          },
        },
      });

      return result;
    },
    [fireGiftCardLoadMutation, fireLegacyMutation, refreshCurrentUser, useNewGiftCardLoadMutation]
  );

  return {
    reloadPrepaidsCardMutation,
    loading: useNewGiftCardLoadMutation ? giftCardLoading : legacyLoading,
  };
};
